/**
 * /policies/shifts/grace-rules — Session 3c PR 11.
 * Owning agent: Tashghil.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { GraceRuleItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function ShiftGraceRulesPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-shift-grace-rules", le],
    queryFn: () => session3cApi.listGraceRules(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<GraceRuleItem>[] = [
    {
      headerKey: "policies.shift_grace.col.scope",
      label: "Scope",
      render: (r) => <div><div className="font-medium text-xs">{r.scope_kind}</div><div className="text-[10px] text-text-tertiary">{r.scope_label_specific}</div></div>,
    },
    {
      headerKey: "policies.shift_grace.col.minutes",
      label: "Grace minutes",
      render: (r) => <span className="font-mono">{r.grace_minutes.value}</span>,
      traceableValue: (r) => ({ label: t("policies.shift_grace.col.minutes", "Grace minutes"), stack: r.grace_minutes }),
    },
    {
      headerKey: "policies.shift_grace.col.escalation",
      label: "Escalation after",
      render: (r) => <span className="font-mono text-xs">{r.escalation_after_per_month}/month</span>,
    },
  ];

  return (
    <PolicyListPage<GraceRuleItem>
      title={t("policies.shift_grace.title", "Late Tolerance / Grace Rules")}
      subtitle={t("policies.shift_grace.subtitle", "Per-shift grace minutes · per-grade override · escalation thresholds.")}
      agent="tashghil"
      domain="shift_grace"
      newButtonLabel={t("policies.shift_grace.cta.new", "New Grace Rule")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.shift_grace.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.shift_grace.suggestion.clean_body", "Grace minutes per scope + escalation count. Escalations route to attendance exceptions for HR review."),
          primaryActionLabel: t("policies.shift_grace.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "shift-grace-rules-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.shift_grace.title", "Late Tolerance / Grace Rules"),
        window_label: t("policies.shift_grace.window", "across shifts + grades"),
      }}
    />
  );
}
